import { Link } from 'react-router-dom';
import { useEffect } from 'react';

import './index.css';

function PrivacyPolicy() {
  useEffect(() => {
    // Enable scrolling
    document.documentElement.style.overflow = 'auto';
    document.documentElement.style.height = 'auto';
    document.body.style.touchAction = 'auto';
    document.documentElement.style.touchAction = 'auto';
    
    return () => {
      // Restore game defaults
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      document.body.style.touchAction = 'none';
      document.documentElement.style.touchAction = 'none';
    };
  }, []);

  return (
    <div className="app content-page">
      <div className="about-content">
        <div className="about-text" style={{ maxWidth: '600px' }}>
          <div className="title-section" style={{ alignItems: 'flex-start' }}>
            <h1>Privacy Policy</h1>
            <small>Last updated: August 2026</small>
          </div>

          <div style={{ fontSize: '14px', lineHeight: 1.8, textAlign: 'left' }}>
            <p>
              Total Ten is developed by Adam Zawierucha. This privacy policy explains how we handle your information when you use our game.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Information We Collect</h3>
            <p>
              Total Ten has no accounts and no login. We never ask for your name, email address, phone number, or any other detail that identifies you, and we have no way to find out who you are.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Local Storage</h3>
            <p>
              Your game history, statistics and preferences are stored locally on your device. Clearing your browser data or uninstalling the app deletes them. On the website that is the only place they ever exist.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>iCloud Sync (iOS app)</h3>
            <p>
              So that your progress and your streak follow you between your iPhone and iPad, the app can save your game history to your own iCloud account using Apple's key-value storage. What is saved is the same thing kept on your device: which boards you played, and when you attempted, completed or cleared each one. It contains nothing that identifies you.
            </p>
            <p>
              This data goes to your private iCloud, not to us. We cannot read it, and we have no access to your iCloud account. Apple handles it under{' '}
              <a target="_blank" rel="noreferrer" href="https://www.apple.com/legal/privacy/">Apple's privacy policy</a>.
            </p>
            <p>
              If you are signed out of iCloud, or you turn iCloud off for Total Ten in your device settings, your history simply stays on the device and the game plays exactly the same. Deleting the app removes its iCloud data along with it. Separately, if you use iCloud Backup, your app data is included in that backup like any other app's.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Gameplay Analytics</h3>
            <p>
              When you start, complete or clear a board, the game sends a short record of that event to our own server at analytics.totalten.io. Each record contains the type of event, the layout of the board, the time it happened, and a random identifier generated on your device the first time you play. We use this to see which boards get solved and to tune the difficulty. The identifier is not linked to you, your device, or anything else about you, and it disappears when you clear your local data.
            </p>
            <p>
              We also use Google Analytics for Firebase to understand how people find the game and how many come back. It records the usual measurements — screens or pages opened, approximate location derived from your IP address, and the kind of device you are using — and reports them to Google, who processes that data on our behalf under{' '}
              <a target="_blank" rel="noreferrer" href="https://business.safety.google/privacy/">Google's privacy policy</a>. It works from an identifier assigned to your app installation or your browser, not from anything that names you.
            </p>
            <p>
              In the iOS app this is the Firebase SDK built into the app. On the website it is the browser version, which you can stop by blocking cookies or using a tracker-blocking extension. The game plays exactly the same either way.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Advertising</h3>
            <p>
              The iOS app shows occasional full-screen ads between boards, served by Google AdMob. Google, not us, collects and processes the data behind those ads — typically device and app information, a coarse location derived from your IP address, and how you interact with an ad. This is described in{' '}
              <a target="_blank" rel="noreferrer" href="https://business.safety.google/privacy/">Google's privacy policy</a> and in{' '}
              <a target="_blank" rel="noreferrer" href="https://policies.google.com/technologies/partner-sites">how Google uses information from sites or apps that use its services</a>. We do not receive any of it.
            </p>
            <p>
              These ads may be personalized, which means Google may use what it knows about your device — including an advertising identifier that can follow you between apps — to decide which ad to show. Where iOS asks whether to allow an app to track you across other companies' apps and websites, your answer controls whether that identifier is available, and you can change it at any time under Settings → Privacy &amp; Security → Tracking. Denying it does not remove the ads; it limits how they are targeted.
            </p>
            <p>
              Buying Total Ten Unlimited removes ads permanently. The version of the game played in a web browser has no ads at all.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Purchases</h3>
            <p>
              Total Ten Unlimited is sold through Apple's In-App Purchase system. Apple handles the payment and tells the app only whether you own the purchase; we never see your payment details.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Third-Party Services</h3>
            <p>
              The iOS app is distributed through Apple's App Store and is governed by Apple's own privacy policy while you are there. We do not receive any personal information from Apple. Google AdMob serves ads inside the iOS app, and Google Analytics for Firebase measures usage in both the app and the website, as described above.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Children's Privacy</h3>
            <p>
              Total Ten is not directed at children under 13, and we do not knowingly collect personal information from them. The game contains nothing beyond arithmetic, but the ads in the iOS app are served by Google and are not curated by us.
            </p>

            <h3 style={{ marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '16px' }}>Changes to This Policy</h3>
            <p>
              We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date.
            </p>

            <p style={{ marginTop: '1.5rem', opacity: 0.6 }}>
              © 2026 <a target="_blank" rel="noreferrer" href="https://zawie.io">Adam Zawierucha</a>. All rights reserved.
            </p>

            <span className="button-container" style={{ justifyContent: 'flex-start', paddingLeft: 0, alignItems: 'center', gap: '12px' }}>
              <Link to="/">
                <button className="play-again-btn">Play Now</button>
              </Link>
              <Link to="/about">
                <button className="try-again-btn">About</button>
              </Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;